import { Currency, DataType, FormatPreset } from '@doss/prisma/types'
import { z } from 'zod'
import { LocaleCode } from '../locales.js'

export enum NumberNegativeStyle {
  STANDARD = 'STANDARD',
  ACCOUNTING = 'ACCOUNTING',
}

const negativeStyle = z.enum([NumberNegativeStyle.STANDARD, NumberNegativeStyle.ACCOUNTING])
export const MIN_NUMBER_DECIMAL_PLACES = 0
export const MAX_NUMBER_DECIMAL_PLACES = 10

const decimalPlaces = z.number().int().min(MIN_NUMBER_DECIMAL_PLACES).max(MAX_NUMBER_DECIMAL_PLACES)
const thousandsSeparator = z.boolean()

// ISO 4217 currency codes (three uppercase letters eg. USD, EUR)
const currency = z.string().regex(/^[A-Z]{3}$/, 'currency must be a 3-letter ISO 4217 code')

// BCP-47 locale tags (min 2 chars)
// Intl.NumberFormat will throw on unrecognized tags on render
const locale = z.string().min(2)

const formatOptionsSchemasByPreset = {
  [FormatPreset.NUMBER_PLAIN]: z
    .object({ decimalPlaces, thousandsSeparator, negativeStyle })
    .strict(),
  [FormatPreset.NUMBER_INTEGER]: z.object({ thousandsSeparator, negativeStyle }).strict(),
  [FormatPreset.NUMBER_DECIMAL]: z
    .object({ decimalPlaces, thousandsSeparator, negativeStyle })
    .strict(),
  [FormatPreset.NUMBER_PERCENT]: z
    .object({ decimalPlaces, thousandsSeparator, negativeStyle })
    .strict(),
  [FormatPreset.NUMBER_CURRENCY]: z
    .object({ currency, locale, negativeStyle, decimalPlaces: decimalPlaces.optional() })
    .strict(),
  [FormatPreset.NUMBER_UNIT]: z
    .object({ unit: z.string().min(1), decimalPlaces, thousandsSeparator })
    .strict(),
  [FormatPreset.DATE_CUSTOM]: z.object({ pattern: z.string() }).strict(),
} as const satisfies Record<FormatPreset, z.ZodTypeAny>

export type FormatOptions<P extends FormatPreset = FormatPreset> = z.infer<
  (typeof formatOptionsSchemasByPreset)[P]
>

export const parseFormatOptions = <P extends FormatPreset>(
  preset: P,
  options: unknown,
): FormatOptions<P> => {
  return formatOptionsSchemasByPreset[preset].parse(options) as FormatOptions<P>
}

export const safeParseFormatOptions = <P extends FormatPreset>(
  preset: P,
  options: unknown,
): z.ZodSafeParseResult<FormatOptions<P>> => {
  return formatOptionsSchemasByPreset[preset].safeParse(options) as z.ZodSafeParseResult<
    FormatOptions<P>
  >
}

export const DEFAULT_FORMAT_OPTIONS: { [P in FormatPreset]: FormatOptions<P> } = {
  [FormatPreset.NUMBER_PLAIN]: {
    decimalPlaces: 4,
    thousandsSeparator: false,
    negativeStyle: NumberNegativeStyle.STANDARD,
  },
  [FormatPreset.NUMBER_INTEGER]: {
    thousandsSeparator: false,
    negativeStyle: NumberNegativeStyle.STANDARD,
  },
  [FormatPreset.NUMBER_DECIMAL]: {
    decimalPlaces: 2,
    thousandsSeparator: false,
    negativeStyle: NumberNegativeStyle.STANDARD,
  },
  [FormatPreset.NUMBER_PERCENT]: {
    decimalPlaces: 2,
    thousandsSeparator: false,
    negativeStyle: NumberNegativeStyle.STANDARD,
  },
  [FormatPreset.NUMBER_CURRENCY]: {
    currency: Currency.USD,
    locale: LocaleCode.EN_US,
    negativeStyle: NumberNegativeStyle.STANDARD,
  },
  [FormatPreset.NUMBER_UNIT]: { unit: 'unit', decimalPlaces: 2, thousandsSeparator: false },
  [FormatPreset.DATE_CUSTOM]: { pattern: '' }, // empty pattern will default to org's locale
}

export const DEFAULT_FORMAT_PRESET_FOR_DATA_TYPE: Partial<Record<DataType, FormatPreset>> = {
  [DataType.NUMBER]: FormatPreset.NUMBER_PLAIN,
  [DataType.FORMULA]: FormatPreset.NUMBER_PLAIN,
  [DataType.FORMULA_NUMBER]: FormatPreset.NUMBER_PLAIN,
  [DataType.DATE]: FormatPreset.DATE_CUSTOM,
  [DataType.DATE_TIME]: FormatPreset.DATE_CUSTOM,
  [DataType.FORMULA_DATE]: FormatPreset.DATE_CUSTOM,
  [DataType.FORMULA_DATE_TIME]: FormatPreset.DATE_CUSTOM,
}

const NUMBER_TYPES: ReadonlySet<DataType> = new Set([
  DataType.NUMBER,
  DataType.FORMULA,
  DataType.FORMULA_NUMBER,
])
const DATE_TYPES: ReadonlySet<DataType> = new Set([
  DataType.DATE,
  DataType.DATE_TIME,
  DataType.FORMULA_DATE,
  DataType.FORMULA_DATE_TIME,
])

type ColumnFormat = {
  formatPreset: FormatPreset | null
  formatOptions: FormatOptions | null
}

/** An organization's formatting defaults, used to fill in options formatOptions does not specify. */
export type OrgFormatDefaults = { locale: LocaleCode; currency: string }

type ResolveColumnFormatInput = {
  type: DataType
  formatPreset?: FormatPreset | null
  // Raw GraphQL JSONObject — variant is unknown until preset is resolved below
  formatOptions?: unknown
  locale?: LocaleCode
  currency?: string
}

export const resolveColumnFormat = (input: ResolveColumnFormatInput): ColumnFormat => {
  const { type } = input
  const isNumber = NUMBER_TYPES.has(type)
  const isDate = DATE_TYPES.has(type)

  if (!isNumber && !isDate) {
    if (input.formatPreset || input.formatOptions) {
      throw new Error(`formatting is not valid on ${type} columns`)
    }
    return { formatPreset: null, formatOptions: null }
  }

  const typeDefault = DEFAULT_FORMAT_PRESET_FOR_DATA_TYPE[type]
  if (!typeDefault) {
    throw new Error(`no default format preset for ${type} columns`)
  }
  const preset = input.formatPreset ?? typeDefault

  let defaultOptions: FormatOptions = DEFAULT_FORMAT_OPTIONS[preset]
  if (preset === FormatPreset.NUMBER_CURRENCY) {
    defaultOptions = {
      currency: input.currency ?? Currency.USD,
      locale: input.locale ?? LocaleCode.EN_US,
      negativeStyle: NumberNegativeStyle.STANDARD,
    }
  }

  const formatOptions = parseFormatOptions(preset, input.formatOptions ?? defaultOptions)
  return { formatPreset: preset, formatOptions }
}
